import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Paper,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearIcon from "@mui/icons-material/Clear";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/store";
import {
  LocationSuggestion,
  SelectedLocation,
} from "../../models/LocationSearch";

interface LocationSearchProps {
  onLocationSelected: (location: SelectedLocation) => void;
  placeholder?: string;
  label?: string;
}

const LocationSearch: React.FC<LocationSearchProps> = observer(
  ({
    onLocationSelected,
    placeholder = "Search location...",
    label = "Location",
  }) => {
    const store = useStore();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [searchInput, setSearchInput] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const debounceTimer = useRef<NodeJS.Timeout | null>(null);

    // Debounced search function
    useEffect(() => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (!searchInput.trim()) {
        store.locationStore.clearLocationSuggestions();
        setShowSuggestions(false);
        return;
      }

      debounceTimer.current = setTimeout(async () => {
        await store.locationStore.searchLocations(searchInput);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      }, 500); // 500ms debounce

      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }, [searchInput, store.locationStore]);

    // Get latitude and longitude from place using Places Details API
    const handleSuggestionClick = async (suggestion: LocationSuggestion) => {
      const { mainText, secondaryText } =
        suggestion.placePrediction.structuredFormat;
      const placeId = suggestion.placePrediction.placeId;

      try {
        const placeDetails = await store.locationStore.getPlaceDetails(placeId);

        if (!placeDetails || !placeDetails.location) {
          console.error(
            "Could not retrieve place details - location missing",
            placeDetails,
          );
          return;
        }

        const { latitude, longitude } = placeDetails.location;

        const selectedLocation: SelectedLocation = {
          placeId,
          mainText: mainText.text,
          secondaryText: secondaryText.text,
          latitude,
          longitude,
        };

        onLocationSelected(selectedLocation);
        setSearchInput("");
        setShowSuggestions(false);
        store.locationStore.clearLocationSuggestions();
      } catch (error) {
        console.error("Error processing location selection:", error);
      }
    };

    // Handle clear button
    const handleClear = () => {
      setSearchInput("");
      store.locationStore.clearLocationSuggestions();
      setShowSuggestions(false);
    };

    return (
      <Box sx={{ position: "relative", width: "100%" }}>
        <TextField
          fullWidth
          label={label}
          placeholder={placeholder}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onFocus={() =>
            searchInput &&
            store.locationStore.getLocationSuggestions().length > 0 &&
            setShowSuggestions(true)
          }
          slotProps={{
            input: {
              endAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {store.locationStore.getLoadingSuggestions() && (
                    <CircularProgress size={20} />
                  )}
                  {searchInput && (
                    <ClearIcon
                      onClick={handleClear}
                      sx={{
                        cursor: "pointer",
                        fontSize: "20px",
                        "&:hover": { color: "error.main" },
                      }}
                    />
                  )}
                </Box>
              ),
            },
          }}
        />

        {showSuggestions &&
          store.locationStore.getLocationSuggestions().length > 0 && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                mt: 1,
                zIndex: 1400,
                maxHeight: isMobile ? "250px" : "350px",
                overflow: "auto",
              }}
            >
              <List disablePadding>
                {store.locationStore
                  .getLocationSuggestions()
                  .map((suggestion, index) => (
                    <Box key={suggestion.placePrediction.placeId}>
                      <ListItemButton
                        onClick={() => handleSuggestionClick(suggestion)}
                        selected={index === selectedIndex}
                        sx={{
                          py: 1.5,
                          "&.Mui-selected": {
                            backgroundColor: "action.selected",
                          },
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <LocationOnIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            suggestion.placePrediction.structuredFormat.mainText
                              .text
                          }
                          secondary={
                            suggestion.placePrediction.structuredFormat
                              .secondaryText.text
                          }
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItemButton>
                      {index <
                        store.locationStore.getLocationSuggestions().length -
                          1 && <Divider />}
                    </Box>
                  ))}
              </List>
            </Paper>
          )}

        {/* No results message */}
        {showSuggestions &&
          !store.locationStore.getLoadingSuggestions() &&
          searchInput.trim() &&
          store.locationStore.getLocationSuggestions().length === 0 && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                mt: 1,
                zIndex: 1400,
                p: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No locations found
              </Typography>
            </Paper>
          )}
      </Box>
    );
  },
);

export default LocationSearch;
